import { ConnectError } from '@bufbuild/connect';
import {
  Alert,
  Button,
  Checkbox,
  Input,
  QRCode,
  Space,
  Typography,
} from 'antd';
import { FC, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { MFARequirementCase } from '@/shared/config';
import { useAppDispatch } from '@/shared/hooks';
import { safeJSONParse } from '@/shared/lib';
import { CodeInput } from '@/shared/ui';

import { authModel } from '@/entities/auth';

import { mfaChallengeMessages as messages } from '../config';

const { Title, Paragraph, Text } = Typography;

const MFA_CODE_LENGTH = 6;

type MFAChallengeProps = {
  requirement: MFARequirementCase;
  onCompleted: () => void;
  onCancel: () => void;
};

/**
 * The second sign-in step for the admin panel. Every admin account is
 * MFA-mandatory on the backend, so without this screen `SignIn` accepts the
 * password and then has nowhere to go.
 */
export const MFAChallenge: FC<MFAChallengeProps> = (props) => {
  const { requirement, onCompleted, onCancel } = props;

  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [isUsingRecoveryCode, setIsUsingRecoveryCode] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEnrolling = requirement === 'EnrollRequired';

  const [secret, setSecret] = useState('');
  const [otpauthURI, setOtpauthURI] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [areCodesSaved, setAreCodesSaved] = useState(false);
  const [signInRequired, setSignInRequired] = useState(false);

  // Each Begin replaces the pending secret server-side, so StrictMode's
  // double-invoked effect must not issue a second one.
  const hasBegunRef = useRef(false);

  // A ref, not the state flag: a click can land in the same tick as the
  // auto-submit on the sixth digit.
  const isSubmittingRef = useRef(false);

  const describeError = (error: unknown): string => {
    const { code: errorCode } = safeJSONParse(
      ConnectError.from(error).rawMessage,
    );

    if (errorCode === 'wrong_code') {
      return formatMessage(messages.wrongCode);
    }

    if (errorCode === 'code_retries_limit') {
      return formatMessage(messages.retriesLimit);
    }

    if (errorCode === 'code_is_expired') {
      return formatMessage(messages.expired);
    }

    return formatMessage(messages.unknownError);
  };

  useEffect(() => {
    if (requirement !== 'EnrollRequired' || hasBegunRef.current) {
      return;
    }

    hasBegunRef.current = true;

    const begin = async () => {
      try {
        const response = await dispatch(
          authModel.thunks.beginMFAEnroll({}),
        ).unwrap();

        setSecret(response.Secret);
        setOtpauthURI(response.OtpauthURI);
      } catch (error) {
        setErrorMessage(describeError(error));
      }
    };

    begin();
  }, [requirement]);

  const verify = async (factor: {
    value: string;
    case: 'TOTPCode' | 'RecoveryCode';
  }) => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      await dispatch(
        authModel.thunks.mfaSignInVerify({ Factor: factor }),
      ).unwrap();

      onCompleted();
    } catch (error) {
      setErrorMessage(describeError(error));
      setCode('');
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const confirmEnrollment = async (enrollmentCode: string) => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const response = await dispatch(
        authModel.thunks.confirmMFAEnroll({ Code: enrollmentCode }),
      ).unwrap();

      setRecoveryCodes(response.RecoveryCodes);
      setSignInRequired(response.SignInRequired);
    } catch (error) {
      setErrorMessage(describeError(error));
      setCode('');
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const changeCode = (nextCode: string) => {
    setCode(nextCode);
    setErrorMessage('');
  };

  const submitCode = (submittedCode: string) => {
    if (isEnrolling) {
      confirmEnrollment(submittedCode);
      return;
    }

    verify({ value: submittedCode, case: 'TOTPCode' });
  };

  if (recoveryCodes.length) {
    return (
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Title level={4}>{formatMessage(messages.recoveryCodesTitle)}</Title>

        <Paragraph type="secondary">
          {formatMessage(messages.recoveryCodesDescription)}
        </Paragraph>

        <Paragraph copyable={{ text: recoveryCodes.join('\n') }}>
          {recoveryCodes.map((recovery) => (
            <div key={recovery}>
              <Text code>{recovery}</Text>
            </div>
          ))}
        </Paragraph>

        <Checkbox
          checked={areCodesSaved}
          onChange={(event) => setAreCodesSaved(event.target.checked)}
        >
          {formatMessage(messages.recoveryCodesSaved)}
        </Checkbox>

        {signInRequired && (
          <Paragraph>{formatMessage(messages.signInRequired)}</Paragraph>
        )}

        <Button
          type="primary"
          disabled={!areCodesSaved}
          onClick={signInRequired ? onCancel : onCompleted}
        >
          {formatMessage(
            signInRequired ? messages.backToSignIn : messages.continueButton,
          )}
        </Button>
      </Space>
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Title level={4}>
        {formatMessage(
          isEnrolling ? messages.enrollTitle : messages.verifyTitle,
        )}
      </Title>

      {isEnrolling ? (
        <>
          <Paragraph type="secondary">
            {formatMessage(messages.enrollDescription)}
          </Paragraph>

          {otpauthURI ? <QRCode value={otpauthURI} size={160} /> : null}

          {secret ? (
            <Paragraph copyable={{ text: secret }}>
              <Text code>{secret}</Text>
            </Paragraph>
          ) : null}
        </>
      ) : (
        <Paragraph type="secondary">
          {formatMessage(
            isUsingRecoveryCode
              ? messages.recoveryDescription
              : messages.verifyDescription,
          )}
        </Paragraph>
      )}

      {isUsingRecoveryCode && !isEnrolling ? (
        <Input
          value={recoveryCode}
          autoFocus
          placeholder="xxxx-xxxx"
          onChange={(event) => {
            setRecoveryCode(event.target.value);
            setErrorMessage('');
          }}
          onPressEnter={() =>
            verify({ value: recoveryCode.trim(), case: 'RecoveryCode' })
          }
        />
      ) : (
        <CodeInput
          value={code}
          length={MFA_CODE_LENGTH}
          error={Boolean(errorMessage)}
          disabled={isSubmitting}
          autoFocus
          onChange={changeCode}
          digitLabel={(position, total) =>
            formatMessage(messages.codeDigitLabel, { position, total })
          }
          onComplete={submitCode}
        />
      )}

      {errorMessage ? (
        <Alert type="error" message={errorMessage} showIcon />
      ) : null}

      <Space>
        <Button
          type="primary"
          loading={isSubmitting}
          disabled={
            isUsingRecoveryCode && !isEnrolling
              ? !recoveryCode.trim()
              : code.length !== MFA_CODE_LENGTH
          }
          onClick={() =>
            isUsingRecoveryCode && !isEnrolling
              ? verify({ value: recoveryCode.trim(), case: 'RecoveryCode' })
              : submitCode(code)
          }
        >
          {formatMessage(messages.confirmButton)}
        </Button>

        {!isEnrolling ? (
          <Button
            type="link"
            disabled={isSubmitting}
            onClick={() => {
              setIsUsingRecoveryCode(!isUsingRecoveryCode);
              setCode('');
              setRecoveryCode('');
              setErrorMessage('');
            }}
          >
            {formatMessage(
              isUsingRecoveryCode
                ? messages.useCodeInstead
                : messages.useRecoveryCode,
            )}
          </Button>
        ) : null}

        <Button type="link" disabled={isSubmitting} onClick={onCancel}>
          {formatMessage(messages.backToSignIn)}
        </Button>
      </Space>
    </Space>
  );
};
